import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { nav, site } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#f9f8f6]/85 backdrop-blur-xl border-b border-black/5"
            : "bg-transparent"
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            className="font-display text-lg md:text-xl tracking-tight leading-none"
            data-testid="nav-logo"
          >
            Lizy Rodríguez
            <span className="hidden sm:inline text-[#9C5A4B]">.</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {nav.slice(0, -1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to === "/" ? "inicio" : item.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide link-underline transition-colors duration-300 ${
                    isActive ? "text-[#9C5A4B]" : "text-[#1a1a1a]/80 hover:text-[#1a1a1a]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/agenda"
              data-testid="nav-cta-agenda"
              className="rounded-full bg-[#9C5A4B] text-[#f9f8f6] text-[13px] px-5 py-2.5 hover:bg-[#8A4A3C] transition-colors duration-300"
            >
              Agenda
            </Link>
          </nav>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#2A2C29] text-[#f9f8f6] lg:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex-1 flex flex-col justify-center px-8 gap-1">
              {nav.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05, duration: 0.5 }}
                >
                  <Link
                    to={item.to}
                    className="font-display text-3xl py-2 block"
                    data-testid={`mobile-nav-${item.to === "/" ? "inicio" : item.to.replace("/", "")}`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="px-8 pb-10 text-sm text-white/60">
              {site.email}
              <br />
              WhatsApp {site.whatsapp}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
